/* =========================================================================
   DynamicVirtualizedTelemetryTable.tsx
   High-performance 2D Virtualized Native HTML Table for Stream Tables
   ('ctable') and Data Tables ('table') across all workspaces.
   Renders cell values strictly on-demand for the visible window.
   ========================================================================= */

import React from 'react';
import { fmtClock, tableColLabel } from '../utils/telemetry-helpers.utils';
import { formatTableCellValue } from '../utils/telemetry-table.utils';
import { useTelemetryTable, UseTelemetryTableOptions } from '../hooks/useTelemetryTable';

export type DynamicVirtualizedTelemetryTableProps = UseTelemetryTableOptions;

export const DynamicVirtualizedTelemetryTable: React.FC<DynamicVirtualizedTelemetryTableProps> = (props) => {
  const { panel, units, precision } = props;
  const {
    containerRef,
    handleScroll,
    visibleCols,
    visibleRowIndices,
    visibleColumns,
    timestamps,
    isStream,
    headerLeft,
    firstColWidth,
    dataColWidth,
    leftPadCols,
    rightPadCols,
    topPadRows,
    bottomPadRows,
    rowHeight,
  } = useTelemetryTable(props);

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      style={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        padding: 0,
        boxSizing: 'border-box',
        background: '#080E12',
      }}
    >
      <table className="td-stream-table" style={{ width: 'max-content', minWidth: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: firstColWidth, minWidth: firstColWidth }}>
              {headerLeft}
            </th>
            {leftPadCols > 0 && (
              <th
                style={{
                  width: leftPadCols * dataColWidth,
                  minWidth: leftPadCols * dataColWidth,
                  padding: 0,
                  border: 'none',
                }}
              />
            )}
            {visibleCols.map((c) => (
              <th
                key={`th-col-${c.index}`}
                style={{
                  width: dataColWidth,
                  minWidth: dataColWidth,
                }}
                title={c.label}
              >
                {c.label}
              </th>
            ))}
            {rightPadCols > 0 && (
              <th
                style={{
                  width: rightPadCols * dataColWidth,
                  minWidth: rightPadCols * dataColWidth,
                  padding: 0,
                  border: 'none',
                }}
              />
            )}
          </tr>
        </thead>
        <tbody>
          {topPadRows > 0 && (
            <tr style={{ height: topPadRows * rowHeight }}>
              <td
                colSpan={visibleCols.length + 1 + (leftPadCols > 0 ? 1 : 0) + (rightPadCols > 0 ? 1 : 0)}
                style={{ height: topPadRows * rowHeight, padding: 0, border: 'none' }}
              />
            </tr>
          )}
          {visibleRowIndices.map((rIdx) => {
            const rowLabel = isStream
              ? fmtClock(timestamps[rIdx])
              : tableColLabel(visibleColumns[rIdx]).replace(/_/g, ' ');

            return (
              <tr key={`tr-row-${rIdx}`} style={{ height: rowHeight }}>
                <td
                  className={isStream ? 'td-st-time' : ''}
                  style={{
                    width: firstColWidth,
                    minWidth: firstColWidth,
                    fontWeight: !isStream ? 600 : undefined,
                    color: !isStream ? '#EEE' : undefined,
                  }}
                  title={rowLabel}
                >
                  {rowLabel}
                </td>
                {leftPadCols > 0 && (
                  <td
                    style={{
                      width: leftPadCols * dataColWidth,
                      minWidth: leftPadCols * dataColWidth,
                      padding: 0,
                      border: 'none',
                    }}
                  />
                )}
                {visibleCols.map(({ index: cIdx }) => {
                  const targetCol = isStream ? visibleColumns[cIdx] : visibleColumns[rIdx];
                  const targetTime = isStream ? timestamps[rIdx] : timestamps[cIdx];

                  const { formattedVal, enumState, rawValForEnum, isPlaceholder } = formatTableCellValue(
                    targetCol,
                    targetTime,
                    panel.id,
                    units,
                    precision
                  );

                  return (
                    <td
                      key={`td-cell-${rIdx}-${cIdx}`}
                      className={isPlaceholder ? 'td-st-ph' : ''}
                      style={{
                        width: dataColWidth,
                        minWidth: dataColWidth,
                      }}
                    >
                      {enumState ? (
                        <>
                          <span className={`td-enum-badge td-enum-badge--${enumState}`}>
                            {enumState.toUpperCase()}
                          </span>
                          {rawValForEnum != null && (
                            <span className="td-enum-raw-val">({rawValForEnum})</span>
                          )}
                        </>
                      ) : (
                        formattedVal
                      )}
                    </td>
                  );
                })}
                {rightPadCols > 0 && (
                  <td
                    style={{
                      width: rightPadCols * dataColWidth,
                      minWidth: rightPadCols * dataColWidth,
                      padding: 0,
                      border: 'none',
                    }}
                  />
                )}
              </tr>
            );
          })}
          {bottomPadRows > 0 && (
            <tr style={{ height: bottomPadRows * rowHeight }}>
              <td
                colSpan={visibleCols.length + 1 + (leftPadCols > 0 ? 1 : 0) + (rightPadCols > 0 ? 1 : 0)}
                style={{ height: bottomPadRows * rowHeight, padding: 0, border: 'none' }}
              />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DynamicVirtualizedTelemetryTable;
